import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'

import S from './Carousel.styled'
import Arrow from './arrow/Arrow'
import Tab from './tab/Tab'
import Slide from './slide/Slide'

const Carousel = ({
  slides = [],
  sbSlides = [],
  autoplaySpeed = 10000,
  sliderDots = false,
  sliderInfinite = true,
  sliderAutoplay = false,
  sliderSpeed = 800,
  slidesToScroll = 1,
  slidesToShow = 1,
}) => {
  const [isAnimationOk, setIsAnimationOk] = useState(true)
  const [isAutoplayOn, setIsAutoplayOn] = useState(true)
  const [isInViewport, setIsInViewport] = useState(true)
  const [isPlaying, setIsPlaying] = useState({ 0: true })
  const [currentSlide, setCurrentSlide] = useState(0)
  const sliderRef = useRef(null)
  const carouselRef = useRef(null)

  const allPresentSlides = sbSlides.length ? sbSlides : slides

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    if (reduceMotion) setIsAnimationOk(false)

    const slider = sliderRef.current
    if (slider) {
      const list = slider.innerSlider && slider.innerSlider.list

      if (list) {
        list.onmouseenter = () => {
          setIsAutoplayOn(false)
        }

        list.onmouseover = () => {
          setIsAutoplayOn(false)
        }

        list.onmouseleave = () => {
          setIsAutoplayOn(true)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (!isInViewport) {
      setIsAutoplayOn(false)
    } else {
      setIsAutoplayOn(true)
    }
  }, [isInViewport])

  const onBarComplete = () => {
    const slider = sliderRef.current

    if (slider && isAnimationOk && allPresentSlides.length > 1) {
      slider.slickNext()
    }
  }

  const onTabClick = (i) => {
    const slider = sliderRef.current
    const dontAnimate = isAnimationOk ? false : true

    if (slider) {
      slider.slickGoTo(i, dontAnimate)
    }
  }

  const observerCallback = useCallback((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting) {
        setIsInViewport(true)
      } else {
        setIsInViewport(false)
      }
    })
  }, [])

  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return null

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.25,
      rootMargin: '60px',
    })

    observer.observe(carousel)

    return () => {
      observer.disconnect()
    }
  }, [observerCallback])

  const settings = {
    dots: sliderDots,
    infinite: sliderInfinite,
    speed: isAnimationOk ? sliderSpeed : 0,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToScroll,
    nextArrow: <Arrow dir="next" />,
    prevArrow: <Arrow dir="prev" />,
    autoplay: sliderAutoplay,
    beforeChange: (currSlide, newSlide) => {
      setCurrentSlide(newSlide)

      if (allPresentSlides[currSlide].template === 'vid_16x9') {
        setIsPlaying({
          ...isPlaying,
          [currSlide]: false,
        })
      }
      if (allPresentSlides[newSlide].template === 'vid_16x9') {
        setIsPlaying({
          ...isPlaying,
          [newSlide]: true,
        })
      }
      if (
        allPresentSlides[currSlide].template === 'vid_16x9' &&
        allPresentSlides[newSlide].template === 'vid_16x9'
      ) {
        setIsPlaying({
          ...isPlaying,
          [currSlide]: false,
          [newSlide]: true,
        })
      }
    },
  }

  return (
    <S.Carousel
      autoplaySpeed={autoplaySpeed}
      isAutoplayOn={isAutoplayOn}
      ref={carouselRef}
    >
      <Slider {...settings} ref={sliderRef}>
        {allPresentSlides.map((slide, i) => {
          const localData = slide.component ? null : slide
          const sbData = slide.component ? slide : null

          return (
            <Slide
              localData={localData}
              sbData={sbData}
              key={slide.id}
              isPlaying={isPlaying}
              i={i}
            />
          )
        })}
      </Slider>
      {allPresentSlides.length > 1 && (
        <Tab
          slides={allPresentSlides}
          setIsAutoplayOn={setIsAutoplayOn}
          onBarComplete={onBarComplete}
          onTabClick={onTabClick}
          currentSlide={currentSlide}
        />
      )}
    </S.Carousel>
  )
}

Carousel.propTypes = {
  slides: PropTypes.array,
  sbSlides: PropTypes.array,
  autoplaySpeed: PropTypes.number,
  sliderDots: PropTypes.bool,
  sliderInfinite: PropTypes.bool,
  slidesToShow: PropTypes.number,
  slidesToScroll: PropTypes.number,
  sliderSpeed: PropTypes.number,
  sliderAutoplay: PropTypes.bool,
}

export default Carousel
